import type { ReactElement } from 'react';
import {
  STORE_DETAIL_DESCRIPTION_ID,
  STORE_DETAIL_PRICE_TIERS_ID,
  STORE_DETAIL_PROCESS_ID,
  STORE_DETAIL_REVIEWS_ID,
} from '@/shared/config';

interface ProductSectionTabsProps {
  /** 수량별 가격 표가 있는지 — 구간이 없는 상품은 바로가기를 달지 않는다. */
  readonly hasPriceTiers: boolean;
  /** 상세 설명 이미지가 있는지 — 없으면 섹션이 없으므로 바로가기도 뺀다. */
  readonly hasDescription: boolean;
}

/**
 * 상세 정보 바로가기 — "제작 정보 | 수량별 가격 | 상세 설명 | 후기" 을 얇은 세로선으로 나눈 작은 줄.
 * 화면 안 앵커 링크라 상태가 없고, 실제로 있는 섹션만 잇는다. "제작 정보" 는 바로 아래 제작 정보 줄이 아니라
 * 페이지 아래의 제작 과정 섹션으로 내려간다. 대상 제목마다 붙어 있는 헤더만큼 띄우는 scroll-mt-28 이 있다.
 */
export const ProductSectionTabs = ({
  hasPriceTiers,
  hasDescription,
}: ProductSectionTabsProps): ReactElement => {
  const links = [
    { id: STORE_DETAIL_PROCESS_ID, label: '제작 정보' },
    hasPriceTiers ? { id: STORE_DETAIL_PRICE_TIERS_ID, label: '수량별 가격' } : null,
    hasDescription ? { id: STORE_DETAIL_DESCRIPTION_ID, label: '상세 설명' } : null,
    { id: STORE_DETAIL_REVIEWS_ID, label: '후기' },
  ].filter((link): link is { id: string; label: string } => link !== null);

  return (
    <nav aria-label="상품 정보 바로가기">
      <ul className="flex flex-wrap items-center text-sm">
        {links.map((link) => (
          <li
            key={link.id}
            className="flex items-center before:mx-3 before:h-3 before:w-px before:bg-foreground/20 first:before:hidden"
          >
            <a
              href={`#${link.id}`}
              className="font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};
